import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { ThemeLocale, getLocale } from '../lib/themeLocale';

export type ThemeName = 'modern' | 'command';

interface ThemeContextValue {
  themeName: ThemeName;
  locale: ThemeLocale;
  setTheme: (name: ThemeName) => void;
  toggleTheme: () => void;
  // Helper to check theme
  isCommand: boolean;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const THEME_STORAGE_KEY = 'cwe-theme';

function getInitialTheme(): ThemeName {
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'modern' || stored === 'command') {
      return stored;
    }
    // Migration: convert old 'imperial' to 'command'
    if (stored === 'imperial') {
      localStorage.setItem(THEME_STORAGE_KEY, 'command');
      return 'command';
    }
  }
  return 'modern';
}

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: ThemeName;
}

export function ThemeProvider({ children, defaultTheme }: ThemeProviderProps) {
  const [themeName, setThemeName] = useState<ThemeName>(defaultTheme || getInitialTheme);

  const locale = useMemo(() => getLocale(themeName), [themeName]);
  const isCommand = themeName === 'command';

  useEffect(() => {
    localStorage.setItem(THEME_STORAGE_KEY, themeName);

    // Update root element class - this triggers all CSS variable changes
    const root = document.documentElement;
    root.classList.remove('theme-modern', 'theme-command', 'theme-imperial');
    root.classList.add(`theme-${themeName}`);
  }, [themeName]);

  const setTheme = useCallback((name: ThemeName) => {
    setThemeName(name);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeName(current => current === 'modern' ? 'command' : 'modern');
  }, []);

  const value = useMemo(
    () => ({ themeName, locale, setTheme, toggleTheme, isCommand }),
    [themeName, locale, setTheme, toggleTheme, isCommand]
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

// Convenience hook for just the locale
// eslint-disable-next-line react-refresh/only-export-components
export function useLocale(): ThemeLocale {
  const { locale } = useTheme();
  return locale;
}
